import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Actions, ofType, createEffect, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, of } from 'rxjs';
import { switchMap, map, tap, catchError, first, filter, take } from 'rxjs/operators';

import { GA4AnalyticsPublicConstants, GA4AnalyticsPublicActions } from '../../GA4/public.api';
import * as AnalyticsActions from '../analytics.actions';
import { getSearchOrigin } from '../analytics.selectors';
import * as AppEventsAnalyticsActions from '../app-events-analytics.actions';

import { ApiSizeInfo, CartItem } from '@api/cart/cart.models';
import { Product, ProductSize } from '@api/catalog/products/products.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { EcommerceContext } from '@core/domain/models/context.models';
import { CurrencyInfo } from '@core/domain/models/store.models';
import {
    getCountryCode, getIsOpenForSale, getLanguageCode, getCurrencyInfo, getPage, getCmsConfigValue,
} from '@core/infrastructure/state/core.selectors';
import { CoreStorageService } from '@core/services/storage/storage.service';
import { CategoriesSelectors } from '@features/category';
import { MenuCategory } from '@features/category/domain/models/menu-category.model';
import { CloseSidebarCustom } from '@features/sidebar/infrastructure/state/sidebar.actions';
import { getSidebarLoadedComponent, isBuyNow } from '@features/sidebar/infrastructure/state/sidebar.selectors';
import { PublicUser } from '@features/user';
import { getWishlistIsEmpty } from '@features/wishlist/infrastructure/state/wishlist.selectors';
import { WishlistPublicActions } from '@features/wishlist/public.api';
import { getCategory, getCategoryId } from '@pages/category/store/category-page.selectors';
import { GiftCardActions } from '@pages/gift-card/store/gift-card.actions';
import { GiftCardSelectors } from '@pages/gift-card/store/gift-card.selector';
import { ClearProductPage } from '@pages/product/store/product-page.actions';
import { getCategoryProduct } from '@pages/product/store/product-page.selectors';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ConfigurationService } from '@shared/analytics/COMMON/services/configuration.service';
import {
    AnalyticsCartAction,
    AnalyticsCartCategory,
    AnalyticsCartCF,
} from '@shared/analytics/constants/cart/analytics-cart.constants';
import { GA4ImActionType } from '@shared/analytics/GA4/models/event-data.model';
import { GA4EventType } from '@shared/analytics/GA4/models/general.model';
import { Ga4SendDatalayer, Ga4Event } from '@shared/analytics/GA4/store/ga4-analytics.actions';
import { sendObservabilityLog } from '@shared/analytics/ITXObservabilityCore/helper';
import { AnalitycsEvent, CartEvent } from '@shared/analytics/models/analytics/analytics.models';
import { AnalyticsCategory, AnalyticsCF, AnalyticsAction } from '@shared/analytics/services/analytics.constants';
import { ImCartService } from '@shared/analytics/services/cart/im-cart.service';
import { getList } from '@shared/analytics/services/data/im-data.helper';
import { imEventAnalyticsEvent } from '@shared/analytics/services/event/im-event.helper';
import { imProductGetCategory } from '@shared/analytics/services/product/analytics-product.helper';
import { ItemSourceListService } from '@shared/analytics/services/product/item-source-list.service';
import { NoOpAction } from '@shared/menu-categories/store/menu-categories.actions';
import { NavigationConstants } from '@shared/navigation/constants/navigation.constants';
import { GoToInternalPage, GoToCheckout, GoToSection } from '@shared/navigation/store/navigation.actions';
import { ProductUtilsService } from '@shared/product/services/product-utils.service';
import { getFromPlace } from '@shared/product/store';
import {
    DeleteItemsShopcart,
    AdjustItemSizeShopcart,
    LoadShopCartSuccess,
} from '@shared/shopcart/store/shopcart.actions';
import { getOrderId, getCartItems } from '@shared/shopcart/store/shopcart.selectors';
import {
    AddProductsShopcartCore,
    AddProductsShopcartCoreSuccess,
    SetItemsInCartSuccess,
} from '@shared/shopcart-core/store/shopcart-core.actions';
import { getPendingGetCartCore, getShopCartCoreItems } from '@shared/shopcart-core/store/shopcart-core.selectors';
import { getUserSelector } from '@shared/user/store';
import { OpenModalFromComponent, CloseModalFromComponent } from '@ui/dialog/store/modal.actions';

@Injectable()
export class AnalyticsCartEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly analyticsCartService: ImCartService,
        private readonly configurationService: ConfigurationService,
        private readonly store: Store,
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly storageService: CoreStorageService,
        private readonly itemSourceListService: ItemSourceListService,
        private readonly translateService: TranslateService,
    ) {
    }

    private readonly getCountryCode$: Observable<string> = this.store.select(getCountryCode).pipe(first());

    private readonly getIsOpenForSale$: Observable<boolean> = this.store.select(getIsOpenForSale).pipe(first());

    private readonly getCurrentLanguageCode$: Observable<string> = this.store.select(getLanguageCode).pipe(first());

    private readonly getCurrencyInfo$: Observable<CurrencyInfo> = this.store.select(getCurrencyInfo).pipe(first());

    private readonly getUser$: Observable<PublicUser> = this.store.select(getUserSelector);

    private readonly getShopcartId$ = this.store.select(getOrderId).pipe(first());

    private readonly getCartItems$: Observable<CartItem[]> = this.store.select(getCartItems);

    private readonly handlePendingCartItems$ = <T>(action: T & { category: AnalyticsCartCategory }): Observable<T> =>
        this.store.select(getPendingGetCartCore).pipe(
            switchMap(pendingItems => {
                if (!!action.category && action.category !== AnalyticsCartCategory.wishList && pendingItems?.length) {
                    return this.actions$.pipe(ofType(SetItemsInCartSuccess)).pipe(first(), map(() => action));
                }
                return of(action);
            }),
        );

    private readonly getCategoriesNavigation$:
    Observable<MenuCategory[]> = this.store.select(CategoriesSelectors.getCategoriesNavigation).pipe(first());

    private readonly getMenuCategories$:
    Observable<MenuCategory[]> = this.store.select(CategoriesSelectors.getMenuCategories).pipe(first());

    /* ################################################### Eventos GA4 ################################################### */

    public EventViewCartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(Ga4SendDatalayer),
            filter(({ dataLayer }) =>
                dataLayer.event === GA4EventType.page_view && /.*(cesta|minicesta).*/.test(dataLayer.context.page_title)),
            concatLatestFrom(() => this.handleViewCartEvent()),
            concatLatestFrom(() => this.store.select(getShopCartCoreItems)),
            filter(([_, shopcartItems]) => !!shopcartItems?.length),
            map(() => Ga4Event({
                name: GA4EventType.view_cart,
                eventFrom: EventFrom.shopcart,
            }))
        );
    });

    private handleViewCartEvent() {
        return this.store.select(getPendingGetCartCore).pipe(
            switchMap(pendingItems => {
                if (!pendingItems.length) return of(true);
                return this.actions$.pipe(ofType(SetItemsInCartSuccess)).pipe(first());
            }),
        );
    }

    public EventAddItemWishlistGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(WishlistPublicActions.AddItemsWishlistAction),
            concatLatestFrom(() => this.store.select(getPage)),
            map(([action, page]) => {
                const eventFromParser = {
                    ItxCategoryPage: EventFrom.parrilla,
                    ItxProductPage: EventFrom.productdetail,
                    ItxAngularShopcartPage: EventFrom.shopcart,
                };

                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.add_to_wishlist,
                    eventFrom: eventFromParser[page],
                    data: {
                        items: action.items as unknown as Product[],
                    },
                });
            })
        );
    });

    public EventAddItemsShopcartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddProductsShopcartCoreSuccess),
            concatLatestFrom(() => this.store.select(getPage)),
            map(([action, page]) => {
                const eventFromParser = {
                    ficha_producto: EventFrom.productdetail,
                    ItxCategoryPage: EventFrom.parrilla,
                    ItxProductPage: EventFrom.parrilla,
                    miniwishlist: EventFrom.miniWishList,
                    productos_relacionados: action?.parentType,
                    wishlist: EventFrom.wishList,
                    ficha_bundle: EventFrom.fichaBundle,
                };
                const products: Product[] = this.analyticsCartService.mapProductsWithRelatedType(action);

                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.add_to_cart,
                    eventFrom: eventFromParser[action?.origin] || eventFromParser[page] || EventFrom.shopcart,
                    data: {
                        imBundleReference: products[0]?.bundleReference || undefined,
                        items: products,
                        place: action?.place,
                        relatedsSectionId: action?.relatedsSectionId,
                    },
                });
            })
        );
    });

    public EventDeleteItemsShopcartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DeleteItemsShopcart),
            map((action) => GA4AnalyticsPublicActions.Ga4Event({
                name: GA4EventType.remove_from_cart,
                eventFrom: EventFrom.shopcart,
                data: {
                    items: action.items,
                    imActionType: GA4AnalyticsPublicConstants.GA4ImActionType.eliminar,
                },
            }))
        );
    });

    public EventEditRemoveItemsShopcartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AdjustItemSizeShopcart),
            map(({ item }) => {
                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.remove_from_cart,
                    eventFrom: EventFrom.shopcart,
                    data: {
                        items: [{ ...item }],
                        imActionType: GA4AnalyticsPublicConstants.GA4ImActionType.modificar_producto_cesta_ok,
                    },
                });
            })
        );
    });

    public EventEditAddItemsShopcartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AdjustItemSizeShopcart),
            map(({ item, size }) => {
                const addedProduct: ApiSizeInfo = {
                    ...item.colors.find((color) => color.id === item.colorId)?.sizes
                        .find((_size) => _size.sku === size.sku),
                };
                delete addedProduct.name;
                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.add_to_cart,
                    eventFrom: EventFrom.shopcart,
                    data: {
                        items: [{ ...item, ...addedProduct } as unknown as CartItem],
                        imActionType: GA4AnalyticsPublicConstants.GA4ImActionType.modificar_producto_cesta_ok,
                    },
                });
            })
        );
    });

    public EventQuantityAddRemoveItemsShopcartGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.ControlCartAnalytics),
            filter((action) => [AnalyticsCartAction.incrementar, AnalyticsCartAction.decrementar].includes(action.action)),
            map((action) => GA4AnalyticsPublicActions.Ga4Event({
                name: action.action === AnalyticsCartAction.incrementar
                    ? GA4EventType.add_to_cart : GA4EventType.remove_from_cart,
                eventFrom: EventFrom.shopcart,
                data: {
                    items: [{
                        ...action.product,
                        quantity: Math.abs(action.extraData?.quantity - (action?.product as CartItem)?.quantity),
                    } as CartItem],
                    imActionType: action.action as unknown as GA4ImActionType,
                },
            }))
        );
    });

    public EventAddToCartGiftcardGA4$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(GiftCardActions.AddToShopCartSuccess),
                concatLatestFrom(() => [
                    this.store.select(GiftCardSelectors.getCard),
                    this.store.select(GiftCardSelectors.getSelectedGiftCardPrice),
                ]),
                map(([, giftCardData, giftCardPrice]) => {
                    return GA4AnalyticsPublicActions.Ga4Event({
                        name: GA4EventType.add_to_cart,
                        eventFrom: EventFrom.giftcard,
                        data: {
                            items: [{ ...giftCardData, ...giftCardPrice }],
                            imActionType: GA4AnalyticsPublicConstants.GA4ImActionType.anadir_gift_card_cesta,
                        },
                    });
                })
            );
        },
    );

    public EventBeginCheckoutGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GoToInternalPage),
            filter(({ url }) => url === NavigationConstants.checkout || url === NavigationConstants.checkoutAuth),
            concatLatestFrom(() => [this.store.select(getPage), this.store.select(isBuyNow)]),
            filter(([_, page, buyNow]) => page !== 'ItxFastSintPage' && !buyNow),
            // REF-AN-1
            concatLatestFrom(() => this.translateService.get('dev.cart.checkout')),
            map(([_, buttonText]) => GA4AnalyticsPublicActions.Ga4Event({
                name: GA4EventType.begin_checkout,
                eventFrom: EventFrom.directo, // NOTE: Recarga de página al acceder al túnel de checkout
                data: {
                    imLinkText: buttonText,
                },
            })),
        );
    });

    public EventBeginCheckoutFastSintGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GoToInternalPage),
            filter(({ url }) => url === NavigationConstants.fastSintStores),
            concatLatestFrom(() => {
                return this.store.select(getCmsConfigValue('clickandcollect_horamax')).pipe(
                    take(1),
                    map(hour => Number(hour)),
                    switchMap((hour) => {
                        // REF-AN-6
                        if (hour === 1) {
                            return this.translateService.get('dev.shopCart.fastSint.pickupOrderHour', { $0: hour });
                        }
                        return this.translateService.get('dev.shopCart.fastSint.pickupOrderHours', { $0: hour });
                    })
                );
            }),
            map(([_, buttonText]) =>
                GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.begin_checkout,
                    eventFrom: EventFrom.fastSint,
                    data: {
                        imLinkText: buttonText,
                    },
                })),
        );
    });

    public EventBeginCheckoutLoginGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GoToCheckout),
            filter(({ checkoutAuth }) => checkoutAuth),
            // REF-AN-1
            concatLatestFrom(() => this.translateService.get('dev.cart.checkout')),
            map(([_, buttonText]) => GA4AnalyticsPublicActions.Ga4Event({
                name: GA4EventType.begin_checkout,
                eventFrom: EventFrom.directo, // NOTE: Recarga de página al acceder al túnel de checkout
                data: {
                    imLinkText: buttonText,
                },
            })),
        );
    });

    public CloseGiftTicketModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenModalFromComponent),
            filter((action) => action.componentName === 'shop-cart-packagin'),
            switchMap(() => this.actions$.pipe(
                ofType(LoadShopCartSuccess)
            ).pipe(first())),
            concatLatestFrom(() => this.store.select(getShopCartCoreItems)),
            filter(([_, shopcartItems]) => !!shopcartItems?.length),
            map(() => GA4AnalyticsPublicActions.Ga4Event({ name: GA4EventType.view_cart, eventFrom: EventFrom['shop-cart-packagin'] }))

        );
    });

    /* ################################################### Eventos UA ################################################### */

    public controlCartAnalyticsEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.ControlCartAnalytics),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            switchMap((action) => {
                return combineLatest([
                    of(action),
                    this.getCurrencyInfo$,
                    this.getShopcartId$,
                ]);
            }),
            tap(([cartEvent, currency, shopcartId]: [CartEvent, CurrencyInfo, number]) => {
                const completeCartEvent: CartEvent = { ...cartEvent };
                completeCartEvent.extraData = { ...cartEvent.extraData, shopcartId };
                const analyticsEvent: AnalitycsEvent = this.analyticsCartService.generateAnalitycsEvent(
                    completeCartEvent,
                    currency
                );
                imEventAnalyticsEvent([analyticsEvent]);
            }),
            map(() => {
                return AnalyticsActions.LoadEventSuccess({
                    payload: {
                        type: 'ControlCartAnalytics',
                        dataObject: {},
                        eventFrom: EventFrom.shopcart,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public addProductsShopcartAnalytics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AppEventsAnalyticsActions.AddProductsShopcartAnalytics),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap((action) => {
                if (action.origin === 'wishlist' || action.origin === 'buylater') {
                    return combineLatest([
                        of(action),
                        this.getCurrencyInfo$,
                        this.getMenuCategories$,
                        this.getCategoriesNavigation$,
                    ]);
                }
                return combineLatest([
                    of(action),
                    this.getCurrencyInfo$,
                    this.getMenuCategories$,
                    this.getCategoriesNavigation$,
                    this.store.select(getCategoryId).pipe(first()),
                    this.store.select(getCategoryProduct).pipe(first()),
                    this.store.select(getFromPlace).pipe(first()),
                    this.store.select(getSearchOrigin).pipe(first()),
                ]);
            }),
            tap(([
                action, currency, categories, categoriesNavigation, categoryId, categoryProductId, fromPlace, searchOrigin,
            ]) => {
                const cartEvent: CartEvent = this.analyticsCartService.generateCartEvent(
                    action,
                    categoryId || categoryProductId,
                    {
                        categories,
                        categoriesNavigation,
                    },
                    fromPlace,
                    searchOrigin,
                );
                const analyticsEvent: AnalitycsEvent = this.analyticsCartService.generateAnalitycsEvent(
                    cartEvent,
                    currency,
                );
                imEventAnalyticsEvent([analyticsEvent]);
            }),
            map(() => {
                return AnalyticsActions.LoadEventSuccess({
                    payload: {
                        type: 'parrilla',
                        dataObject: {},
                        eventFrom: EventFrom.parrilla,
                    },
                });
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError(() => {
                return of(AnalyticsActions.LoadEventError());
            })
        );
    });

    public controlCartPageviewAnalyticsEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AnalyticsActions.ControlCartPageviewAnalytics),
            switchMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            concatLatestFrom((action) => [
                this.handlePendingCartItems$<typeof action>(action),
                this.getCurrentLanguageCode$,
                this.getCountryCode$,
                this.getIsOpenForSale$,
                this.getUser$,
                this.getCurrencyInfo$,
                this.getCartItems$,
            ]),
            map(([
                _,
                { cf, category, extraData },
                currentLanguageCode,
                countryCode,
                isOpenForSale,
                user,
                currencyInfo,
                cartItems,
            ]) => {
                try {
                    const context = {
                        store: {
                            countryCode,
                            isOpenForSale,
                        },
                        currentLanguage: {
                            code: currentLanguageCode,
                        },
                    } as EcommerceContext;
                    this.analyticsCartService.createView(
                        context,
                        category,
                        cf,
                        user,
                        currencyInfo,
                        cartItems,
                        extraData,
                    );

                    return AnalyticsActions.LoadEventSuccess({
                        payload: {
                            type: 'PageViewEvent',
                            dataObject: {},
                            eventFrom: EventFrom.shopcart,
                        },
                    });
                } catch (e) {
                    sendObservabilityLog(e, 'controlcartpageviewAnalyticsEvent$');
                    return AnalyticsActions.LoadEventError();
                }
            }),
        );
    });

    public addProductsShopcartAnalaytics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddProductsShopcartCore),
            map((action) => {
                if (action.location === 'related_product') {
                    const cf = action.origin === AnalyticsCategory.parrilla
                        ? AnalyticsCF.catalog : AnalyticsCF.checkout;
                    const category = imProductGetCategory(
                        action?.baseProduct,
                        action?.place,
                        action?.location
                    );
                    return AnalyticsActions.AddToCartEvent({
                        data: {
                            cf,
                            category,
                            action: AnalyticsAction.addToCartProductoCross,
                            product: {
                                ...action.products[0],
                            },
                            extraData: {
                                parentType: action?.parentType,
                            },
                            needsEcommerce: true,
                            categoryId: action.categoryId,
                            actionType: 'add',
                            baseProduct: action.baseProduct,
                            place: action.place,
                            location: action.location,
                        },
                    });
                }
                return AppEventsAnalyticsActions.AddProductsShopcartAnalytics(action);
            }),
        );
    });

    public ComingSoonBackSoonModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenModalFromComponent),
            filter((action) => action.componentName === 'product-coming-soon-modal'),
            concatLatestFrom(() => this.store.select(getPage)),
            map(([action, page]) => {
                const product: Product = action.data?.product;
                const productSize: ProductSize = product?.status?.selectedSize;
                const partnumber = productSize?.partnumber;
                const comingSoonBackSoon = productSize?.visibilityValue?.toLowerCase();
                const pageData = {
                    ItxCategoryPage: { pageType: 'parrilla', eventFrom: EventFrom.parrilla },
                    ItxProductPage: { pageType: 'ficha_producto', eventFrom: EventFrom.productdetail },
                }[page];
                if (pageData) {
                    return AnalyticsActions.BasicPopUpView({
                        section: 'catalogo',
                        pageType: pageData?.pageType,
                        pageTitle: `${partnumber}/${comingSoonBackSoon}`,
                        eventFrom: pageData?.eventFrom,
                        products: [product],
                    });
                }
                return AnalyticsActions.LoadEventError();
            })
        );
    },);

    public relatedProductSourceListTracker$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                AppEventsAnalyticsActions.BundleClickAnalytics,
                AppEventsAnalyticsActions.RelatedProductClickAnalytics
            ),
            concatLatestFrom(() => [
                this.store.select(getPage),
                this.store.select(getCategory),
                this.store.select(getSidebarLoadedComponent),
            ]),
            tap(([action, page, category, sidebarLoadedComponent]) => {
                const analyticsAction = {
                    [AppEventsAnalyticsActions.BundleClickAnalytics.type]: AnalyticsAction.verBundle,
                    [AppEventsAnalyticsActions.RelatedProductClickAnalytics.type]: AnalyticsAction.RelatedProductClick,
                }[action.type];
                const eventData = { ...action.data };
                const list = getList(eventData.product, page, category, this.window, this.document, this.storageService);
                const analyticsCategory = imProductGetCategory(
                    eventData.product,
                    eventData.place,
                    eventData.location
                );
                // IMPORTANTE: Añadir nuevos tipos de relacionados en esta lógica
                this.itemSourceListService.setItemSourceList(
                    list,
                    analyticsCategory,
                    analyticsAction,
                    eventData.parentType,
                    eventData.product,
                    page,
                    eventData.baseProduct,
                    eventData.place,
                    sidebarLoadedComponent
                );
            })
        );
    }, { dispatch: false });

    public clearItemSourceList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ClearProductPage),
            tap(() => {
                this.itemSourceListService.clearItemSourceList();
            })
        );
    }, { dispatch: false });

    public sendPopupAddToCartPageView$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AddProductsShopcartCoreSuccess),
            filter(({ openPopup, page }) => openPopup && page !== 'ItxAngularShopcartPage'),
            map((
                action
            ) => {
                const partNumberColor: string = ProductUtilsService.getPartNumberColor(
                    action.products[0]?.reference,
                    action.products[0]?.colors[0]?.id,
                );
                return AnalyticsActions.BasicPopUpView({
                    section: 'catalogo',
                    pageType: action.origin,
                    pageTitle: `${partNumberColor}/productos_relacionados`,
                    eventFrom: EventFrom[action.origin],
                    products: action.products,
                    parentType: action.parentType,
                });
            }),
        );
    });

    public modalOrSidebarClosePageView$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(CloseModalFromComponent, CloseSidebarCustom),
            concatLatestFrom(() => [this.store.select(getPage), this.store.select(getWishlistIsEmpty)]),
            map(([_action, page, wishlistEmpty]) => {
                switch (page) {
                    case 'ItxProductPage':
                        return AnalyticsActions.SendProductPagePageView();
                    case 'ItxCategoryPage':
                        return AnalyticsActions.SendParrillaPageView();
                    case 'ItxAngularWishlistPage':
                        return AnalyticsActions.ControlCartPageviewAnalytics({
                            cf: AnalyticsCartCF.catalogo,
                            category: AnalyticsCartCategory.wishList,
                            extraData: {
                                pageTitle: wishlistEmpty ? 'vacia' : undefined,
                            },
                        });
                    default:
                        return NoOpAction();
                }
            }),
        );
    });

    public trackGoToShopCart$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GoToSection),
            filter(action => action.url === 'shopCart'),
            map(() => AppEventsAnalyticsActions.ControlCartAnalytics({
                cf: AnalyticsCartCF.checkout,
                category: AnalyticsCartCategory.miniCesta,
                action: AnalyticsCartAction.irCesta,
                needsEcommerce: false, // Ajusta este valor según tus necesidades
            }))
        ));
}
